import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";

import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid2"; // Updated to Grid2
import FullscreenHero from "../components/hero/hero";
import ApplicationForm from "../components/form/form";

import { fetchJobDetails } from "../controllers/job-controllers";
import { createContext } from "react";

import JobDescription from "../components/job-details/job-description";
import JobBenefits from "../components/job-details/job-benefits";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { trackTotalVisits } from "../controllers/job-controllers";
import { Helmet } from "react-helmet";
import { formatMoney, formatTimeUnit } from "../utils/formatters";
import moment from "moment";

export const PageContext = createContext({});

export const PageConsumer = PageContext.Consumer;

export default function JobDetails({ company }) {
  const params = useParams();
  const job_id = params.id;
  const [jobData, setJobData] = useState(null);

  useEffect(() => {
    //Save if already visited job for job tracking only once per user
    const visitedJobs = JSON.parse(localStorage.getItem("visitedJobs")) || [];

    if (!visitedJobs.includes(job_id)) {
      trackTotalVisits(job_id);
      localStorage.setItem(
        "visitedJobs",
        JSON.stringify([...visitedJobs, job_id])
      );
    }

    fetchJobDetails(job_id).then((res) => {
      if (res?.error) {
        console.log(res?.error);
      } else {
        setJobData(res?.data);
      }
    });
  }, []);

  const memoizedValue = useMemo(
    () => ({
      jobData,
      setJobData,
    }),
    [jobData, setJobData]
  );

  const salaryFrom = formatMoney(
    jobData?.jobDetails?.salary?.from,
    jobData?.jobDetails?.currency || "SEK"
  );

  const salaryTo = formatMoney(
    jobData?.jobDetails?.salary?.to,
    jobData?.jobDetails?.currency || "SEK"
  );

  const timeUnit = formatTimeUnit(jobData?.jobDetails?.timeUnit);

  return (
    <>
      <Helmet>
        <title>
          {company?.name
            ? `${company?.name} | ${jobData?.jobDetails?.title}`
            : jobData?.jobDetails?.title}
        </title>
      </Helmet>
      <PageContext.Provider value={memoizedValue}>
        <FullscreenHero company={company} jobData={jobData?.jobDetails} />

        {/* Main Content Section with Sticky Sidebar */}
        <Container
          maxWidth="lg"
          sx={{
            mt: 5,
            display: "flex",
            gap: { xs: "20px", md: "80px" },
            flexDirection: "column",
            marginTop: { xs: 0, md: "-80px" },
            backgroundColor: "white",
            position: "relative",
            zIndex: "10",
            borderRadius: { xs: "0px", md: "20px" },
            boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px",
            padding: {
              xs: "40px 15px!important", // Up to md
              md: "80px 100px 80px 100px!important",
            },
          }}
        >
          <Stack
            direction="row"
            sx={{
              gap: { xs: 2, md: "60px" },
              flexDirection: {
                xs: "column",
                md: "row",
              },
            }}
          >
            <Stack
              minWidth="20%"
              alignItems="flex-end"
              sx={{
                alignItems: {
                  xs: "flex-start",
                  md: "flex-end",
                },
                display: {
                  xs: "none",
                  md: "flex",
                },
              }}
            >
              <Typography fontSize="24px" fontWeight={600}>
                Om tjänsten
              </Typography>
            </Stack>

            <Grid spacing={8} flexGrow={1}>
              {/* Left Side (Main Content) */}
              <Grid size={{ xs: 12, md: 7 }}>
                <Box>
                  <Stack
                    alignItems="start"
                    justifyContent="space-between"
                    sx={{
                      flexDirection: {
                        xs: "column-reverse",
                        md: "row",
                      },
                      marginBottom: { xs: 2, md: 0 },
                      gap: { md: 2, xs: 1 },
                    }}
                  >
                    <Stack>
                      <Typography variant="h4">
                        {jobData?.jobDetails?.title}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Divider
                    sx={{
                      display: { xs: "block", md: "none" },
                      marginBottom: 2,
                    }}
                  />

                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    sx={{
                      gap: { xs: "10px", md: 4 },
                      margin: { xs: "10px 0 20px 0", md: "10px 0 20px 0" },
                    }}
                  >
                    {jobData?.jobDetails?.location && (
                      <>
                        <Stack direction="row" gap={1} alignItems="center">
                          <img
                            src="/assets/icons/location.svg"
                            alt="location icon"
                          />
                          <Typography fontWeight={600}>
                            {jobData.jobDetails.location}
                          </Typography>
                        </Stack>

                        {/* <Divider orientation="vertical"  flexItem /> */}
                      </>
                    )}

                    {!jobData?.jobDetails?.hideSalary &&
                      jobData?.jobDetails?.salary && (
                        <>
                          <Stack direction="row" gap={1} alignItems="center">
                            <img
                              src="/assets/icons/money.svg"
                              alt="money icon"
                            />
                            {jobData.jobDetails.salaryRange ? (
                              <Typography fontWeight={600}>
                                {salaryFrom} ‑ {salaryTo} / {timeUnit}
                              </Typography>
                            ) : (
                              <Typography fontWeight={600}>
                                {jobData.jobDetails.salary.static}{" "}
                                {jobData.jobDetails.currency} / {timeUnit}
                              </Typography>
                            )}
                          </Stack>
                          {/* <Divider orientation="vertical" flexItem /> */}
                        </>
                      )}

                    {/* {jobData?.jobDetails?.typeOfContract && (
                      <Stack direction="row" gap={1} alignItems="center">
                        <img src="/assets/icons/bag.svg" alt="bag icon" />
                        <Typography fontWeight={600}>
                          {jobData?.jobDetails?.typeOfContract}
                        </Typography>
                      </Stack>
                    )} */}
                  </Stack>

                  <Divider
                    sx={{
                      display: { xs: "block", md: "none" },
                      marginBottom: 2,
                    }}
                  />

                  <Stack direction="row" gap={1} alignItems="center">
                    <Typography
                      sx={{
                        fontSize: { xs: "12px", md: "14px" },
                        color: "#9e9e9e",
                      }}
                    >
                      Publicerad:{" "}
                      {moment(jobData?.jobDetails?.createdAt).format(
                        "MMM D, YYYY"
                      )}
                    </Typography>
                  </Stack>

                  {/* Job decsription */}
                  <JobDescription
                    description={jobData?.jobDetails?.description}
                  />
                </Box>
              </Grid>
            </Grid>
          </Stack>

          <Divider />

          <Stack
            direction="row"
            sx={{
              flexDirection: {
                xs: "column",
                md: "row",
              },
              gap: { xs: 2, md: "60px" },
            }}
          >
            <Stack
              minWidth="20%"
              alignItems="flex-end"
              sx={{
                alignItems: {
                  xs: "flex-start",
                  md: "flex-end",
                },
              }}
            >
              <Typography fontSize="24px" fontWeight={600}>
                Om företaget
              </Typography>
            </Stack>

            <Stack flexGrow={1}>
              <Stack gap={2}>
                {company?.name && company?.name !== "" && (
                  <>
                    <Stack>
                      <Typography variant="subtitle" fontWeight={"bold"}>
                        Namn
                      </Typography>
                      <Typography>{company?.name}</Typography>
                    </Stack>
                  </>
                )}

                {company?.companyEmail && company?.companyEmail !== "" && (
                  <>
                    <Divider />
                    <Stack>
                      <Typography variant="subtitle" fontWeight={"bold"}>
                        Email
                      </Typography>
                      <Typography>{company?.companyEmail}</Typography>
                    </Stack>

                    <Divider />
                  </>
                )}

                {company?.companyPhone && company?.companyPhone !== "" && (
                  <>
                    <Stack>
                      <Typography variant="subtitle" fontWeight={"bold"}>
                        Telefon
                      </Typography>
                      <Typography>{company?.companyPhone}</Typography>
                    </Stack>

                    <Divider />
                  </>
                )}

                {company?.companyCountry && company?.companyCountry !== "" && (
                  <>
                    <Stack>
                      <Typography variant="subtitle" fontWeight={"bold"}>
                        Land
                      </Typography>
                      <Typography>{company?.companyCountry}</Typography>
                    </Stack>
                  </>
                )}

                {company?.companyDescription &&
                  company?.companyDescription !== "" && (
                    <>
                      <Divider />
                      <Stack>
                        <Typography variant="subtitle" fontWeight={"bold"}>
                          Företagsbeskrivning
                        </Typography>
                        <div
                          className="company-description"
                          dangerouslySetInnerHTML={{
                            __html: company?.companyDescription,
                          }}
                        ></div>
                      </Stack>
                    </>
                  )}
                {company?.companyIndustry &&
                  company?.companyIndustry !== "" && (
                    <>
                      <Divider />
                      <Stack>
                        <Typography variant="subtitle" fontWeight={"bold"}>
                          Industri
                        </Typography>
                        <Typography>{company?.companyIndustry}</Typography>
                      </Stack>
                    </>
                  )}
              </Stack>
            </Stack>
          </Stack>

          {jobData?.jobDetails?.benefits &&
            jobData?.jobDetails?.benefits.length > 0 && (
              <>
                <Divider />
                <Stack
                  direction="row"
                  sx={{
                    flexDirection: {
                      xs: "column",
                      md: "row",
                    },
                    gap: { xs: 2, md: "60px" },
                    marginBottom: { xs: 3, md: 0 },
                  }}
                >
                  <Stack
                    minWidth="20%"
                    alignItems="flex-end"
                    sx={{
                      alignItems: {
                        xs: "flex-start",
                        md: "flex-end",
                      },
                    }}
                  >
                    <Typography fontSize="24px" fontWeight={600}>
                      Our benefits
                    </Typography>
                  </Stack>

                  <Stack flexGrow={1}>
                    <JobBenefits
                      benefits={jobData?.jobDetails?.benefits}
                      primary_color={jobData?.jobDetails?.theme?.color.primary}
                      secondary_color={
                        jobData?.jobDetails?.theme?.color.secondary
                      }
                    />
                  </Stack>
                </Stack>
              </>
            )}

          {jobData?.jobDetails?.requiredSkills &&
            jobData?.jobDetails?.requiredSkills.length > 0 && (
              <Stack
                direction="row"
                sx={{
                  flexDirection: {
                    xs: "column",
                    md: "row",
                  },
                  gap: { xs: 2, md: "60px" },
                }}
              >
                <Stack
                  minWidth="20%"
                  alignItems="flex-end"
                  sx={{
                    alignItems: {
                      xs: "flex-start",
                      md: "flex-end",
                    },
                  }}
                >
                  <Typography fontSize="24px" fontWeight={600}>
                    Required Skills
                  </Typography>
                </Stack>

                <Stack flexGrow={1}>
                  <JobBenefits
                    benefits={jobData?.jobDetails?.requiredSkills}
                    primary_color={jobData?.jobDetails?.theme?.color.primary}
                    secondary_color={
                      jobData?.jobDetails?.theme?.color.secondary
                    }
                  />
                </Stack>
              </Stack>
            )}
        </Container>

        <Box
          sx={{
            backgroundColor: "var(--c-main)", // Use primary color from the theme
          }}
        >
          <Container id={"apply"} sx={{ mt: 9, display: "flex", padding: 0 }}>
            {/* Application Form */}
            <ApplicationForm
              job_id={job_id}
              jobData={jobData}
              jobQuestions={jobData?.screeningQuestions?.questions}
            />
          </Container>
        </Box>
      </PageContext.Provider>
    </>
  );
}
